/**
 * SliderItemEditor - Individual slide editor component
 *
 * Edits: image (always), title, subtitle1, subtitle2 (conditional based on visibility).
 * Includes two-thumb range slider for duration control (like A/B test weights).
 *
 * Key behavior: Moving a boundary only affects TWO adjacent items, not all items.
 *
 * Pattern adapted from VariantWeightEditor in A/B test editor.
 */

import React, { useState, useLayoutEffect, useCallback, memo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { StyledTextField, MediaUploadField } from '@modules/ui-components';
import { DurationRangeSlider } from './DurationRangeSlider';
import type { SliderItemEditorProps } from '../../../../types/sliderSection.types';

/**
 * Local text input with blur-based sync
 * Prevents focus loss during parent re-renders
 */
interface LocalTextInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  label: string;
  placeholder?: string;
  testId: string;
  multiline?: boolean;
  rows?: number;
}

const LocalTextInput = memo<LocalTextInputProps>(function LocalTextInput({
  value,
  onChange,
  disabled,
  label,
  placeholder,
  testId,
  multiline = false,
  rows = 1,
}) {
  const [localValue, setLocalValue] = useState(value);

  // Sync local state when prop changes from external source
  useLayoutEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = useCallback(() => {
    if (localValue !== value) {
      onChange(localValue);
    }
  }, [localValue, value, onChange]);

  return (
    <StyledTextField
      value={localValue}
      onChange={(newValue) => setLocalValue(newValue)}
      onBlur={handleBlur}
      disabled={disabled}
      size="small"
      fullWidth
      placeholder={placeholder || label}
      aria-label={label}
      multiline={multiline}
      rows={rows}
      inputProps={{
        'data-testid': testId,
      }}
    />
  );
});

/**
 * SliderItemEditor Component
 *
 * Layout (updated with range slider on separate row):
 * [#N] [Link icon if connected] [Delete]
 * [====== Range Slider (two thumbs) ======]
 * [Range: 0 - 33 (33%) • 5.0s]
 * [Image Upload - full width - always visible]
 * [Title TextField - conditional based on visibility]
 * [Subtitle 1 TextField - conditional based on visibility]
 * [Subtitle 2 TextField - conditional based on visibility]
 */
export const SliderItemEditor: React.FC<SliderItemEditorProps> = ({
  index,
  title,
  subtitle1,
  subtitle2,
  imageUrl,
  durationPercent,
  range,
  totalDurationSeconds,
  isFirst,
  isLast,
  hasConnections,
  visibility,
  onTitleChange,
  onSubtitle1Change,
  onSubtitle2Change,
  onImageChange,
  onImageUpload,
  onRangeChange,
  onDelete,
  canDelete,
  disabled,
  renderTitleField,
  renderSubtitle1Field,
  renderSubtitle2Field,
}) => {
  const theme = useTheme();

  /**
   * Handle image change (URL or File)
   * Follows pattern from ListItemEditor
   */
  const handleImageChange = useCallback(
    async (value: string | File | null) => {
      if (value instanceof File) {
        try {
          const url = await onImageUpload(value);
          onImageChange(url);
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      } else {
        onImageChange(value);
      }
    },
    [onImageUpload, onImageChange]
  );

  return (
    <Box
      data-testid={`slider-item-editor-${index}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        p: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
      }}
    >
      {/* Row 1: Index + Connection indicator + Delete */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              minWidth: 24,
            }}
          >
            #{index + 1}
          </Typography>
          {hasConnections && (
            <LinkIcon
              fontSize="small"
              sx={{ color: 'primary.main' }}
              titleAccess="Has timer action"
            />
          )}
        </Box>

        <IconButton
          data-testid={`slider-item-${index}-delete`}
          aria-label={`Delete slide ${index + 1}`}
          onClick={onDelete}
          disabled={disabled || !canDelete}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Row 2: Range Slider - FULL WIDTH SEPARATE ROW */}
      <DurationRangeSlider
        value={range}
        onRangeChange={onRangeChange}
        totalDurationSeconds={totalDurationSeconds}
        isFirst={isFirst}
        isLast={isLast}
        disabled={disabled}
        testId={`slider-item-${index}-duration`}
      />

      {/* Image Upload - Always visible for slider */}
      {visibility.showImage && (
        <Box data-testid={`slider-item-${index}-image`}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Image
          </Typography>
          <MediaUploadField
            value={imageUrl || null}
            onChange={handleImageChange}
            mediaType="image"
            disabled={disabled}
            size="small"
            showPreview
            placeholder="Upload slide image"
          />
        </Box>
      )}

      {/* Title - Conditional based on visibility */}
      {visibility.showTitle && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Title
          </Typography>
          {renderTitleField ? renderTitleField({
            index,
            value: title,
            onChange: onTitleChange,
            disabled,
            label: 'Title',
            placeholder: 'Enter slide title',
            testId: `slider-item-${index}-title`,
          }) : (
            <LocalTextInput
              value={title}
              onChange={onTitleChange}
              disabled={disabled}
              label="Title"
              placeholder="Enter slide title"
              testId={`slider-item-${index}-title`}
            />
          )}
        </Box>
      )}

      {/* Subtitle 1 - Conditional based on visibility */}
      {visibility.showSubtitle1 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Subtitle 1
          </Typography>
          {renderSubtitle1Field ? renderSubtitle1Field({
            index,
            value: subtitle1,
            onChange: onSubtitle1Change,
            disabled,
            label: 'Subtitle 1',
            placeholder: 'Enter subtitle 1',
            testId: `slider-item-${index}-subtitle1`,
            multiline: true,
            rows: 2,
          }) : (
            <LocalTextInput
              value={subtitle1}
              onChange={onSubtitle1Change}
              disabled={disabled}
              label="Subtitle 1"
              placeholder="Enter subtitle 1"
              testId={`slider-item-${index}-subtitle1`}
              multiline
              rows={2}
            />
          )}
        </Box>
      )}

      {/* Subtitle 2 - Conditional based on visibility */}
      {visibility.showSubtitle2 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Subtitle 2
          </Typography>
          {renderSubtitle2Field ? renderSubtitle2Field({
            index,
            value: subtitle2,
            onChange: onSubtitle2Change,
            disabled,
            label: 'Subtitle 2',
            placeholder: 'Enter subtitle 2',
            testId: `slider-item-${index}-subtitle2`,
            multiline: true,
            rows: 2,
          }) : (
            <LocalTextInput
              value={subtitle2}
              onChange={onSubtitle2Change}
              disabled={disabled}
              label="Subtitle 2"
              placeholder="Enter subtitle 2"
              testId={`slider-item-${index}-subtitle2`}
              multiline
              rows={2}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default SliderItemEditor;
